import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { CurrentUser, GeoServer, LoginInput, RegisterInput } from '@sirocco/shared';
import * as firebaseAuth from '../firebase/auth';
import { setCurrentServerId } from '../firebase/context';
import { clearScreenCache } from '../hooks/useAsync';
import { clearScrollMemory } from '../hooks/useScrollMemory';
import { clearTabMemory } from '../hooks/useTabMemory';
import { enregistrerLesAppels, oublierLesAppels } from '../firebase/appelSysteme';
import { enregistrerCetAppareil, oublierCetAppareil } from '../native/notifications';
import * as profiles from '../firebase/profiles';
import {
  readAmbiancePreference,
  rememberAmbiancePreference,
  resolveAmbiance,
  watchSystemAmbiance,
  type Ambiance,
  type AmbiancePreference,
} from '../theme/ambiance';
import { applyServerTheme, DEFAULT_THEME } from '../theme/applyTheme';

/**
 * Session : qui est connecte, et sur quel spot.
 *
 * C'est le seul etat reellement global de l'application. Le reste des
 * donnees est charge par ecran, au plus pres de son usage.
 *
 * La session n'est plus un jeton que l'on garde soi-meme : Firebase la
 * restaure au demarrage et previent par `watchSession`. On se contente
 * d'ecouter.
 */

interface SessionValue {
  user: CurrentUser | null;
  server: GeoServer | null;
  /** true tant que Firebase n'a pas dit s'il y a une session. */
  loading: boolean;
  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
  /** Rejoint un spot et repeint l'application a ses couleurs. */
  joinServer: (serverId: string) => Promise<void>;
  /** Remplace l'utilisateur en memoire apres une modification du profil. */
  setUser: (user: CurrentUser) => void;
  /** Jour ou nuit, tel que c'est peint : la preference deja resolue. */
  ambiance: Ambiance;
  /** Ce que le membre a choisi, « systeme » compris. */
  ambiancePreference: AmbiancePreference;
  setAmbiancePreference: (preference: AmbiancePreference) => void;
}

const SessionContext = createContext<SessionValue | null>(null);

/**
 * Intervalle entre deux signes de vie.
 * Plus court que la fenetre de cinq minutes de `toPublicUser` : sans
 * marge, un membre actif clignoterait entre "en ligne" et "absent".
 */
const HEARTBEAT_MS = 2 * 60 * 1000;

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<CurrentUser | null>(null);
  const [server, setServer] = useState<GeoServer | null>(null);
  const [loading, setLoading] = useState(true);
  /*
    La preference est lue avant tout appel reseau : sans elle, un membre
    en mode nuit verrait l'application s'ouvrir en blanc le temps que son
    profil arrive. Elle est locale a l'appareil — « suivre le systeme »
    ne veut rien dire d'un telephone a l'autre.
  */
  const [ambiancePreference, setPreferenceState] =
    useState<AmbiancePreference>(readAmbiancePreference);
  const [ambiance, setAmbianceState] = useState<Ambiance>(() =>
    resolveAmbiance(readAmbiancePreference()),
  );

  // Le theme du spot courant, garde sous la main pour pouvoir le
  // repeindre quand l'heure change sans relire le catalogue.
  const themeRef = useRef(DEFAULT_THEME);
  const ambianceRef = useRef(ambiance);
  ambianceRef.current = ambiance;

  /**
   * Charge le spot d'un membre et applique son theme.
   * Le retient aussi pour la couche donnees, qui en a besoin a chaque
   * lecture sans que les ecrans aient a le transporter.
   */
  const loadServer = useCallback(async (serverId: string | null, mode: Ambiance) => {
    // Changer de spot change tout ce qu'un ecran affiche : ce qui reste
    // du precedent ne doit pas reapparaitre une fraction de seconde.
    clearScreenCache();
    clearScrollMemory();
    clearTabMemory();
    setCurrentServerId(serverId);
    if (!serverId) {
      setServer(null);
      themeRef.current = DEFAULT_THEME;
      applyServerTheme(DEFAULT_THEME, mode);
      return;
    }
    try {
      const geoServer = await profiles.getServer(serverId);
      setServer(geoServer);
      themeRef.current = geoServer.theme;
      applyServerTheme(geoServer.theme, mode);
    } catch {
      // Un spot retire de la base ne doit pas bloquer l'application.
      setServer(null);
      themeRef.current = DEFAULT_THEME;
      applyServerTheme(DEFAULT_THEME, mode);
    }
  }, []);

  // Applique l'heure courante des le premier rendu, puis a chaque bascule.
  useEffect(() => {
    applyServerTheme(themeRef.current, ambiance);
  }, [ambiance]);

  /**
   * Signe de vie, pour que "en ligne" veuille dire quelque chose.
   *
   * `lastSeenAt` est relu par `toPublicUser` avec une fenetre de cinq
   * minutes : on le rafraichit un peu plus souvent, et a chaque retour au
   * premier plan — une application mise en veille sur iOS ne fait plus
   * tourner ses minuteurs, et son membre doit s'eteindre tout seul.
   */
  useEffect(() => {
    if (!user) return;

    const beat = () => {
      if (document.visibilityState === 'hidden') return;
      profiles.touchLastSeen().catch(() => {
        // Un signe de vie perdu n'est pas une erreur a montrer.
      });
    };

    beat();
    const timer = setInterval(beat, HEARTBEAT_MS);
    document.addEventListener('visibilitychange', beat);

    /*
      Et la presence par connexions, en plus du battement : c'est elle qui
      sait dire « parti » en secondes — le serveur efface la connexion des
      que le lien casse. Le battement reste pour les lecteurs qui ne
      parlent pas encore ce langage.
    */
    const finPresence = profiles.signalerPresence();

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', beat);
      finPresence();
    };
  }, [user?.id]);

  /**
   * Bascule jour / nuit.
   * On repeint tout de suite, on retient localement, et on previent le
   * profil pour que le reglage suive le compte sur un autre appareil.
   */
  const setAmbiancePreference = useCallback((next: AmbiancePreference) => {
    const peint = resolveAmbiance(next);
    setPreferenceState(next);
    setAmbianceState(peint);
    rememberAmbiancePreference(next);
    profiles
      .setNightMode(peint === 'night')
      .then(setUserState)
      .catch(() => {
        // Le reglage vaut pour cet appareil meme si l'ecriture echoue.
      });
  }, []);

  /*
    Le telephone bascule, l'application suit — mais seulement si c'est ce
    qu'on lui a demande. Le reglage peut changer sans quitter l'app :
    depuis le centre de controle, ou tout seul au coucher du soleil quand
    la bascule automatique d'iOS est active.
  */
  useEffect(() => {
    if (ambiancePreference !== 'system') return;
    setAmbianceState(resolveAmbiance('system'));
    return watchSystemAmbiance(setAmbianceState);
  }, [ambiancePreference]);

  // Firebase decide : au demarrage il restaure la session s'il en a une,
  // et rappelle a chaque connexion ou deconnexion.
  useEffect(() => {
    let cancelled = false;

    const stop = firebaseAuth.watchSession(
      (account) => {
        if (cancelled) return;
        setUserState(account);
        /*
          L'apparence est celle de l'appareil, pas celle du compte : elle
          est deja resolue depuis le premier rendu, bien avant que ce
          profil arrive. Restaurer une session ne doit pas la changer.
        */
        void loadServer(account?.currentServerId ?? null, ambianceRef.current).finally(() => {
          if (!cancelled) setLoading(false);
        });
      },
      () => {
        if (!cancelled) setLoading(false);
      },
    );

    return () => {
      cancelled = true;
      stop();
    };
  }, [loadServer]);

  /*
    L'appareil s'enregistre des qu'une session existe.

    Ici plutot que dans `login` : on entre aussi par l'inscription, et par
    la session deja ouverte au lancement. Un seul endroit couvre les
    trois, et l'enregistrement se refait a chaque ouverture — un jeton
    change tout seul, et le reecrire ne coute qu'une ligne.
  */
  const monId = user?.id;
  useEffect(() => {
    if (!monId) return;
    void enregistrerCetAppareil();
    // Le jeton des appels vit dans une autre branche : il vient d'un autre
    // service d'Apple, et les deux ne s'echangent pas.
    void enregistrerLesAppels();
  }, [monId]);

  const login = useCallback(
    async (input: LoginInput) => {
      const account = await firebaseAuth.login(input);
      setUserState(account);
      /*
        L'apparence ne vient plus du compte mais de l'appareil : se
        connecter ne doit pas rallumer le blanc sur un telephone en mode
        sombre. Le compte garde `nightMode` — d'autres surfaces le liront
        peut-etre — mais il ne commande plus rien ici.
      */
      await loadServer(account.currentServerId, ambianceRef.current);
    },
    [loadServer],
  );

  const register = useCallback(async (input: RegisterInput) => {
    const account = await firebaseAuth.register(input);
    clearScreenCache();
    clearScrollMemory();
    clearTabMemory();
    setUserState(account);
    // Un nouveau membre n'a pas encore de spot : il ira le choisir.
    setCurrentServerId(null);
    setServer(null);
  }, []);

  const logout = useCallback(async () => {
    // Avant de fermer la session : l'oubli s'ecrit en base, il lui faut
    // encore une identite. La presence en fait partie — se deconnecter
    // n'est pas partir, le lien reseau reste ouvert et le serveur
    // n'effacerait donc jamais la connexion de lui-meme.
    await oublierCetAppareil();
    await oublierLesAppels();
    await profiles.retirerLaPresence();
    await firebaseAuth.logout();
    clearScreenCache();
    clearScrollMemory();
    clearTabMemory();
    setUserState(null);
    setCurrentServerId(null);
    setServer(null);
    themeRef.current = DEFAULT_THEME;
    applyServerTheme(DEFAULT_THEME, ambianceRef.current);
  }, []);

  const joinServer = useCallback(
    async (serverId: string) => {
      const account = await profiles.joinServer(serverId);
      setUserState(account);
      await loadServer(serverId, ambianceRef.current);
    },
    [loadServer],
  );

  const value = useMemo<SessionValue>(
    () => ({
      user,
      server,
      loading,
      login,
      register,
      logout,
      joinServer,
      setUser: setUserState,
      ambiance,
      ambiancePreference,
      setAmbiancePreference,
    }),
    [
      user,
      server,
      loading,
      login,
      register,
      logout,
      joinServer,
      ambiance,
      ambiancePreference,
      setAmbiancePreference,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession doit être utilisé dans un SessionProvider');
  return context;
}
